/**
 * useWechatJSSDKInitialize.ts
 *
 * A custom React hook for initializing and managing WeChat JSSDK.
 * This hook handles:
 * - WeChat JSSDK initialization with signature verification
 * - State management for the WeChat SDK instance
 * - Error handling and logging
 *
 * Prerequisites:
 * - WeChat public account configured
 * - Backend API endpoint for signature generation
 * - wechat-jssdk package installed
 *
 * @see https://github.com/JasonBoy/wechat-jssdk
 */

import { useCallback, useRef, useState } from 'react'
import { Platform } from 'react-native'
import { useWechatSignatureMutation } from '../generated/graphql'
import { Env } from '../env'
import * as Sentry from '@sentry/react-native'

const WechatJSSDK = require('wechat-jssdk/dist/client.umd')

export type WechatJSSDKInstance = InstanceType<typeof WechatJSSDK>

export type WechatJSSDKConfig = {
  appId: string
  nonceStr: string
  signature: string
  timestamp: string
  debug?: boolean
  jsApiList: Array<string>
}

/**
 * Interface for the return value of initializeWechatJSSDK
 */
interface WechatJSSDKInitResult {
  /** The initialized WeChat JSSDK instance, or null if initialization failed */
  instance: WechatJSSDKInstance | null
}

/**
 * Initializes WeChat JSSDK with signature verification.
 * This function handles the complete initialization flow:
 * 1. Fetches signature from backend API
 * 2. Configures WeChat JSSDK with signature data
 * 3. Initializes the SDK instance
 *
 * @param params - Initialization parameters
 * @param params.sign - GraphQL mutation function for fetching signature
 * @param params.url - Current page URL (must match the URL used for signature generation)
 * @param params.jsApiList - List of WeChat JS APIs to initialize
 * @returns Promise resolving to the initialization result
 */
export async function initializeWechatJSSDK(params: {
  sign: ReturnType<typeof useWechatSignatureMutation>[0]
  url: string
  jsApiList: Array<string>
}): Promise<WechatJSSDKInitResult> {
  const { sign, url, jsApiList } = params

  if (Platform.OS !== 'web') {
    return { instance: null }
  }

  try {
    const result = await sign({
      variables: {
        input: {
          url: encodeURIComponent(url),
        },
      },
    })

    if (!result.data?.wechatSignature) {
      throw new Error('Failed to get WeChat signature data from server')
    }

    const wechatSignature = result.data.wechatSignature

    const config: WechatJSSDKConfig = {
      appId: Env.WECHAT_PUBLIC_ACCOUNT_ID,
      nonceStr: wechatSignature.nonceStr,
      signature: wechatSignature.signature,
      timestamp: wechatSignature.timestamp,
      debug: Env.IS_DEVELOP,
      jsApiList,
    }

    const wechatObj = new WechatJSSDK(config)
    const initializedInstance = await wechatObj.initialize()

    return { instance: initializedInstance }
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : 'Unknown initialization error'
    console.error('[initializeWechatJSSDK] Initialization failed:', {
      error: errorMessage,
      url,
      jsApiList,
    })
    Sentry.captureException(err, {
      tags: {
        component: 'WeChatJSSDK',
        operation: 'initialize',
      },
      extra: {
        url,
        jsApiList,
      },
    })
    return { instance: null }
  }
}

/**
 * Internal hook to initialize WeChat JSSDK and manage the SDK instance state.
 *
 * @internal
 * This hook is used internally by WechatJSSDKProvider.
 * For public usage, use the `useWechatJSSDK` hook from the context provider instead.
 *
 * @returns Object containing:
 *   - wxInstance: The initialized WeChat JSSDK instance, or null if not initialized
 *   - isReady: Boolean indicating if the SDK is ready to use
 *   - isInitializing: Boolean indicating if initialization is in progress
 *   - initialize: Function to manually trigger initialization
 *
 * @see useWechatJSSDK from '../contexts/wechat-jssdk.provider' for public API
 */
export const useWechatJSSDKInitialize = () => {
  const [doSignature] = useWechatSignatureMutation()
  const [wxInstance, setWxInstance] = useState<WechatJSSDKInstance | null>(null)
  const [isInitializing, setIsInitializing] = useState(false)
  const initAttemptedRef = useRef(false)

  const initialize = useCallback(
    async (url: string, jsApiList: Array<string>) => {
      if (Platform.OS !== 'web') {
        return null
      }

      if (initAttemptedRef.current || isInitializing || wxInstance) {
        return wxInstance
      }

      initAttemptedRef.current = true
      setIsInitializing(true)

      try {
        const { instance } = await initializeWechatJSSDK({
          sign: doSignature,
          url,
          jsApiList,
        })

        if (instance) {
          setWxInstance(instance)
          return instance
        }
        return null
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error'
        console.error(
          `[useWechatJSSDKInitialize] Initialization error: ${errorMessage}`
        )
        Sentry.captureException(err, {
          tags: {
            component: 'useWechatJSSDKInitialize',
            operation: 'initialize',
          },
        })
        return null
      } finally {
        setIsInitializing(false)
      }
    },
    [doSignature, isInitializing, wxInstance]
  )

  const isReady = wxInstance !== null

  return {
    wxInstance,
    isReady,
    isInitializing,
    initialize,
  }
}
